import React, { useEffect, useState } from 'react';
import { SlidersHorizontal, X } from 'lucide-react';
import { applyLoungeFilters, DEFAULT_LOUNGE_FILTERS } from '../featureModels';
import { formatCurrency } from '../demoMode';

const GENDER_FILTERS = [
  { label: 'All', value: 'all' },
  { label: 'Women', value: 'woman' },
  { label: 'Men', value: 'man' },
  { label: 'Non-binary', value: 'non-binary' },
];

const TIERS = [
  { value: 'verified', label: 'Verified' },
  { value: 'premium', label: 'Premium' },
  { value: 'base', label: 'Signature' },
];

export function FilterIcon({ size = 20 }) {
  return <SlidersHorizontal size={size} strokeWidth={1.8} aria-hidden="true" />;
}

export default function FilterSheet({ open, people, filters, onApply, onClose }) {
  const [draft, setDraft] = useState(filters || DEFAULT_LOUNGE_FILTERS);

  useEffect(() => {
    if (open) {
      setDraft(filters || DEFAULT_LOUNGE_FILTERS);
    }
  }, [open, filters]);

  if (!open) {
    return null;
  }

  const toggleTier = (tier) => {
    setDraft((current) => ({
      ...current,
      tiers: current.tiers.includes(tier) ? current.tiers.filter((t) => t !== tier) : [...current.tiers, tier],
    }));
  };

  const draftCount = applyLoungeFilters(people, draft).length;

  return (
    <>
      <div className="ic-filter-overlay" onClick={onClose} />
      <div className="ic-filter-sheet" role="dialog" aria-label="Filters">
        <div className="ic-filter-sheet-head">
          <button type="button" className="ic-filter-reset" onClick={() => setDraft(DEFAULT_LOUNGE_FILTERS)}>
            Reset
          </button>
          <h3>Filters</h3>
          <button type="button" className="ic-filter-close" onClick={onClose} aria-label="Close filters">
            <X size={20} strokeWidth={1.8} aria-hidden="true" />
          </button>
        </div>

        <div className="ic-filter-sheet-body">
          <div className="ic-filter-group">
            <p className="ic-filter-label">Show me</p>
            <div className="ic-filter-pill-row">
              {GENDER_FILTERS.map(({ label, value }) => (
                <button
                  key={value}
                  type="button"
                  className={`ic-filter-pill ${draft.gender === value ? 'active' : ''}`}
                  onClick={() => setDraft((current) => ({ ...current, gender: value }))}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="ic-filter-group">
            <div className="ic-filter-row-between">
              <p className="ic-filter-label">Age</p>
              <p className="ic-filter-value">{draft.minAge}–{draft.maxAge}</p>
            </div>
            <div className="ic-filter-slider-row">
              <div className="ic-filter-slider-col">
                <p className="ic-filter-slider-caption">Min</p>
                <input
                  type="range"
                  min={18}
                  max={80}
                  step={1}
                  value={draft.minAge}
                  onChange={(event) =>
                    setDraft((current) => ({ ...current, minAge: Math.min(Number(event.target.value), current.maxAge - 1) }))
                  }
                />
              </div>
              <div className="ic-filter-slider-col">
                <p className="ic-filter-slider-caption">Max</p>
                <input
                  type="range"
                  min={18}
                  max={80}
                  step={1}
                  value={draft.maxAge}
                  onChange={(event) =>
                    setDraft((current) => ({ ...current, maxAge: Math.max(Number(event.target.value), current.minAge + 1) }))
                  }
                />
              </div>
            </div>
          </div>

          <div className="ic-filter-group">
            <div className="ic-filter-row-between">
              <p className="ic-filter-label">Distance</p>
              <p className="ic-filter-value">{draft.maxDistanceKm} km</p>
            </div>
            <input
              type="range"
              aria-label="Maximum distance"
              min={1}
              max={100}
              step={1}
              value={draft.maxDistanceKm}
              onChange={(event) => setDraft((current) => ({ ...current, maxDistanceKm: Number(event.target.value) }))}
            />
          </div>

          <div className="ic-filter-group">
            <div className="ic-filter-row-between">
              <p className="ic-filter-label">Max rate</p>
              <p className="ic-filter-value">{formatCurrency(draft.maxRate)}/hr</p>
            </div>
            <input
              type="range"
              min={0}
              max={200}
              step={1}
              value={draft.maxRate}
              onChange={(event) => setDraft((current) => ({ ...current, maxRate: Number(event.target.value) }))}
            />
          </div>

          <div className="ic-filter-group">
            <p className="ic-filter-label">Member tier</p>
            <div className="ic-filter-tier-row">
              {TIERS.map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  className={`ic-filter-tier-btn ${draft.tiers.includes(value) ? 'active' : ''}`}
                  onClick={() => toggleTier(value)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="ic-filter-sheet-foot">
          <button type="button" className="ic-filter-apply" onClick={() => onApply(draft)}>
            Show {draftCount} result{draftCount !== 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </>
  );
}
